import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Package, Truck, Mail, Lock, User, Phone, Building2 } from "lucide-react";

/**
 * Registration form component with user type selection
 */
export function RegisterForm({ 
  registerForm, 
  setRegisterForm, 
  onSubmit, 
  loading,
  getGoogleAuthUrl 
}) {
  return (
    <div className="border border-border p-6 space-y-6">
      <div className="text-center">
        <h1 className="text-2xl font-bold mb-2">Create Account</h1>
        <p className="text-muted-foreground text-sm">Join Setuxpress today</p>
      </div>

      <form onSubmit={onSubmit} className="space-y-4">
        {/* User Type Selection */}
        <div className="space-y-2">
          <Label>I want to</Label>
          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => setRegisterForm({ ...registerForm, user_type: "user" })}
              className={`p-4 border-2 transition-all ${
                registerForm.user_type === "user"
                  ? "border-accent bg-accent/5"
                  : "border-border hover:border-accent/50"
              }`}
              data-testid="select-user"
            >
              <Package className="w-8 h-8 mx-auto mb-2 text-accent" />
              <p className="font-semibold text-sm">Ship Cargo</p>
            </button>
            <button
              type="button"
              onClick={() => setRegisterForm({ ...registerForm, user_type: "partner" })}
              className={`p-4 border-2 transition-all ${
                registerForm.user_type === "partner"
                  ? "border-primary bg-primary/5"
                  : "border-border hover:border-primary/50"
              }`}
              data-testid="select-partner"
            >
              <Truck className="w-8 h-8 mx-auto mb-2 text-primary" />
              <p className="font-semibold text-sm">List Space</p>
            </button>
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="register-name">Full Name</Label>
          <div className="relative">
            <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              id="register-name"
              type="text"
              placeholder="John Doe"
              value={registerForm.name}
              onChange={(e) => setRegisterForm({ ...registerForm, name: e.target.value })}
              required
              className="pl-10 rounded-none"
              data-testid="register-name"
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="register-email">Email</Label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              id="register-email"
              type="email"
              placeholder="you@example.com"
              value={registerForm.email}
              onChange={(e) => setRegisterForm({ ...registerForm, email: e.target.value })}
              required
              className="pl-10 rounded-none"
              data-testid="register-email"
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="register-phone">Phone Number</Label>
          <div className="relative">
            <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              id="register-phone"
              type="tel"
              placeholder="+91 9876543210"
              value={registerForm.phone}
              onChange={(e) => setRegisterForm({ ...registerForm, phone: e.target.value })}
              className="pl-10 rounded-none"
              data-testid="register-phone"
            />
          </div>
        </div>

        {/* Partner-specific fields */}
        {registerForm.user_type === "partner" && (
          <>
            <div className="space-y-2">
              <Label htmlFor="register-company">Company Name *</Label>
              <div className="relative">
                <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="register-company"
                  type="text"
                  placeholder="Your company name"
                  value={registerForm.company_name}
                  onChange={(e) => setRegisterForm({ ...registerForm, company_name: e.target.value })}
                  required
                  className="pl-10 rounded-none"
                  data-testid="register-company"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Business Type</Label>
              <Select
                value={registerForm.company_type}
                onValueChange={(value) => setRegisterForm({ ...registerForm, company_type: value })}
              >
                <SelectTrigger className="rounded-none" data-testid="register-company-type">
                  <SelectValue placeholder="Select business type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="logistics">Logistics Company</SelectItem>
                  <SelectItem value="jet_owner">Private Jet Owner</SelectItem>
                  <SelectItem value="local_delivery">Local Delivery Partner</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </>
        )}

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label htmlFor="register-password">Password</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                id="register-password"
                type="password"
                placeholder="••••••••"
                value={registerForm.password}
                onChange={(e) => setRegisterForm({ ...registerForm, password: e.target.value })}
                required
                className="pl-10 rounded-none"
                data-testid="register-password"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="register-confirm">Confirm</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                id="register-confirm"
                type="password"
                placeholder="••••••••"
                value={registerForm.confirmPassword}
                onChange={(e) => setRegisterForm({ ...registerForm, confirmPassword: e.target.value })}
                required
                className="pl-10 rounded-none"
                data-testid="register-confirm"
              />
            </div>
          </div>
        </div>

        <Button
          type="submit"
          className="w-full rounded-none btn-hover bg-accent text-accent-foreground"
          disabled={loading || !registerForm.user_type}
          data-testid="register-submit"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create Account"}
        </Button>
      </form>

      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-border"></div>
        </div>
        <div className="relative flex justify-center text-xs uppercase">
          <span className="bg-background px-2 text-muted-foreground">Or continue with</span>
        </div>
      </div>

      <Button
        type="button"
        variant="outline"
        className="w-full rounded-none gap-2"
        onClick={() => window.location.href = getGoogleAuthUrl()}
        data-testid="google-signup"
      >
        <svg className="w-4 h-4" viewBox="0 0 24 24">
          <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
          <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
          <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
          <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
        </svg>
        Sign up with Google
      </Button>
    </div>
  );
}
